import React, { useMemo } from 'react';

import Avatar from '@atlaskit/avatar';
import { rovoHexIcon } from '@atlaskit/logo/raw-icons';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { fireErrorAnalytics } from '@atlassian/error-handling';

import type { ChildrenProps } from './AgenticPlanWrapperInChat';
import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';

const RovoHeaderIcon = () => (
	<Avatar
		size="small"
		name="Rovo"
		appearance="hexagon"
		src={rovoHexIcon}
		borderColor={token('elevation.surface')}
		testId="rovo-header-icon"
	/>
);

type Props = ActionRendererFor<typeof ActionKeys.MultipleChoiceQuestionsTool>;

type AskQuestionQuestions = ActionRendererFor<
	typeof ActionKeys.AskQuestion
>['actions'][number]['data']['questions'];

type OptionAnswer = { optionIndex: number; type: 'option' };
type CustomAnswer = { text: string; type: 'custom' };
type AutoAnswer = { type: 'auto' };
type QuestionAnswer = OptionAnswer | CustomAnswer | AutoAnswer;

export type AskQuestionCompletedViewAnswerState = Record<number, QuestionAnswer | undefined>;

export type AskQuestionCompletedViewProps = {
	answers: AskQuestionCompletedViewAnswerState;
	cloudId: Props['cloudId'];
	ContentRenderer: Props['ContentRenderer'];
	headerIcon?: React.ReactNode;
	productKey: Props['productKey'];
	questions: AskQuestionQuestions;
};

type AskQuestionAnswers = AskQuestionCompletedViewAnswerState;

type MultipleChoiceOption = {
	description?: string;
	id: string;
	label: string;
};

type MultipleChoiceQuestion = {
	id: string;
	options: MultipleChoiceOption[];
	question: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isString = (value: unknown): value is string => typeof value === 'string';

// Parses and validates questions from raw data, returning typed MultipleChoiceQuestion[].
const parseQuestions = (data: unknown): MultipleChoiceQuestion[] => {
	if (!isRecord(data) || !Array.isArray(data.questions)) {
		return [];
	}
	return data.questions.flatMap((q) => {
		if (!isRecord(q) || !isString(q.id) || !isString(q.question) || !Array.isArray(q.options)) {
			return [];
		}
		const options = q.options.flatMap((opt) => {
			if (!isRecord(opt) || !isString(opt.id) || !isString(opt.label)) {
				return [];
			}
			return [
				{
					id: opt.id,
					label: opt.label,
					description: isString(opt.description) ? opt.description : undefined,
				},
			];
		});
		return [{ id: q.id, question: q.question, options }];
	});
};

// Extracts answer map {questionId → answerId} from a questions array.
export const parseAnswersByQuestionId = (data: unknown): Record<string, string | null> => {
	if (!isRecord(data) || !Array.isArray(data.questions)) {
		return {};
	}
	return data.questions.reduce<Record<string, string | null>>((acc, q) => {
		if (!isRecord(q) || !isString(q.id)) {
			return acc;
		}
		const { answer } = q;
		if (answer == null || isString(answer)) {
			acc[q.id] = (answer as string | null) ?? null;
		}
		return acc;
	}, {});
};

// Converts parsed questions into the shape expected by AskQuestionCompletedView.
const toCompletedView = (
	questions: MultipleChoiceQuestion[],
	answersByQuestionId: Record<string, string | null>,
): { answers: AskQuestionAnswers; questions: AskQuestionQuestions } => {
	const completedQuestions: AskQuestionQuestions = [];
	const answers: AskQuestionAnswers = {};

	questions.forEach((q, idx) => {
		completedQuestions.push({
			header: '',
			question: q.question,
			options: q.options.map((opt) => ({ label: opt.label, description: opt.description ?? '' })),
		});

		const answer = answersByQuestionId[q.id];
		if (answer == null) {
			answers[idx] = { type: 'auto' };
			return;
		}
		const optionIndex = q.options.findIndex((opt) => opt.id === answer);
		answers[idx] =
			optionIndex >= 0 ? { type: 'option', optionIndex } : { type: 'custom', text: answer };
	});

	return { questions: completedQuestions, answers };
};

export const MultipleChoiceAnswers = ({
	action,
	confirmedAnswers,
	ContentRenderer,
	messageId,
	productKey,
	cloudId,
	AskQuestionCompletedView,
}: {
	action: ChildrenProps['message']['actions'][0];
	AskQuestionCompletedView: React.ComponentType<AskQuestionCompletedViewProps>;
	cloudId: Props['cloudId'];
	confirmedAnswers: Record<string, string | null>;
	ContentRenderer: Props['ContentRenderer'];
	messageId: string;
	productKey: Props['productKey'];
}): React.JSX.Element => {
	const sourceQuestions = useMemo(() => parseQuestions(action?.data), [action]);

	const executedAnswers = useMemo(() => {
		const executedData = action?.executedData;
		if (!isRecord(executedData) || !isString(executedData.arguments)) {
			return {};
		}

		try {
			// executedData.arguments is a JSON string containing questions with answers embedded
			const executedArgs = JSON.parse(executedData.arguments);
			return parseAnswersByQuestionId(executedArgs);
		} catch {
			fireErrorAnalytics({
				meta: {
					id: 'multipleChoiceAnswers',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: { error: 'Invalid executedData for multiple choice answers', messageId },
			});
			return {};
		}
	}, [action, messageId]);

	const { questions, answers } = useMemo(() => {
		const answersByQuestionId =
			Object.keys(executedAnswers).length > 0 ? executedAnswers : confirmedAnswers;
		return toCompletedView(sourceQuestions, answersByQuestionId);
	}, [sourceQuestions, executedAnswers, confirmedAnswers]);

	return (
		<AskQuestionCompletedView
			questions={questions}
			answers={answers}
			ContentRenderer={ContentRenderer}
			productKey={productKey}
			cloudId={cloudId}
			headerIcon={fg('jira_ai_task_level_planning_ootb_agent') ? undefined : <RovoHeaderIcon />}
		/>
	);
};
